import React, { useContext, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import { LoadingContext, M_MessageBoard, M_Button, M_Select, BufferService } from 'src/M_Components'
import './utils.css'

export default function TableShipmentCharges({
    value = [],
    shipments = [],
    onOpenCreate,
    onOpenEdit,
    onRefresh,
    isView = false,
}) {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const shipmentChargesService = new BufferService('ShipmentCharges')

    const [selectedShipmentId, setSelectedShipmentId] = useState('')
    const [selectedRow, setSelectedRow] = useState(null)
    const [chargeToDisable, setChargeToDisable] = useState(null)
    const [disabling, setDisabling] = useState(false)

    const primaryColor   = ctx?.primaryColor  || 'var(--color-blue)'
    const secondaryColor = ctx?.secondaryColor || 'var(--color-black)'

    const hStyle = {
        borderBottom: `2px solid ${primaryColor}`,
        color: secondaryColor,
        textAlign: 'center',
    }

    const cell = (rowData, field) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '42px', padding: '0 10px' }}>
            <span>{rowData[field] ?? ''}</span>
        </div>
    )

    // symbolField can be a rowData key ('CostCurrencySymbol') or a literal string ('€').
    const decimalCell = (rowData, field, symbolField = 'CurrencySymbol') => {
        const sym = symbolField in rowData ? rowData[symbolField] : symbolField
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '42px', padding: '0 10px' }}>
                <span>
                    {rowData[field] != null
                        ? `${Number(rowData[field]).toFixed(2)}${sym ? ' ' + sym : ''}`
                        : ''}
                </span>
            </div>
        )
    }

    const deleteCell = (rowData) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '42px', padding: '0 4px' }}>
            <Tooltip title={t('Delete')}>
                <CancelIcon className='buttonColorRed cursorPointer' style={{ transform: 'scale(.6)' }} onClick={() => setChargeToDisable(rowData)} />
            </Tooltip>
        </div>
    )

    const confirmDisable = () => {
        if (!chargeToDisable) return
        setDisabling(true)
        shipmentChargesService.delete(chargeToDisable.Id).then((res) => {
            setDisabling(false)
            if (res?.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('ErrorDeleting'))
            } else {
                ctx.toastMessages('success', '', t('SuccessShipmentChargeDeleted'))
                if (selectedRow?.Id === chargeToDisable.Id) setSelectedRow(null)
                if (onRefresh) onRefresh()
            }
            setChargeToDisable(null)
        })
    }

    const hasShipmentDropdown = shipments.length > 0 && onOpenCreate
    const canEdit = !!selectedRow && !!onOpenEdit

    const sumField = (field) => value.reduce((acc, row) => acc + (Number(row[field]) || 0), 0).toFixed(2)
    const totalInvoiceSum = sumField('TotalInvoice')
    const totalCostSum = sumField('TotalCost')
    const revenueSum = sumField('Revenue')

    const shipmentCell = (rowData) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '42px', padding: '0 10px' }}>
            <span>{rowData.ShipmentTransportKey != null ? `${rowData.ShipmentTransportKey}` : ''}</span>
        </div>
    )

    const columns = [
        { field: 'ServiceTypeName', header: t('Service') },
        { field: 'Goal', header: t('Goal') },
        { field: 'PayableHow', header: t('PayableHow') },
        { field: 'Nature', header: t('Nature') },
        { field: 'CurrencyCode', header: t('Currency') },
        { field: 'Quantity', header: t('Quantity') },
    ]

    return (
        <div className='divPrincipalTable'>
            {(hasShipmentDropdown || onOpenEdit) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px' }}>
                    {/* Shipment selector */}
                    {hasShipmentDropdown && (
                        <div className='selectBooking'>
                            <M_Select
                                label={t('Shipment')}
                                value={selectedShipmentId}
                                onChange={e => setSelectedShipmentId(e.target.value)}
                                options={shipments.map(s => ({ label: `${t('Shipment')} ${s.TransportKey}`, value: s.ShipmentId }))}
                            />
                        </div>
                    )}

                    {/* Create — requires a shipment selected */}
                    {onOpenCreate && (
                        <div style={{ opacity: selectedShipmentId ? 1 : 0.4, pointerEvents: selectedShipmentId ? 'auto' : 'none' }}>
                            <M_Button
                                hoverEffect
                                normal
                                bold
                                label={t('Create')}
                                icon={<img src='/icons/svgsDaNet/plus-square.svg' alt={t('Create')} style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)' }} className='iconCreateBooking' />}
                                borderColor={'var(--color-grey)'}
                                onClick={() => {
                                    onOpenCreate(selectedShipmentId)
                                    setSelectedShipmentId('')
                                }}
                            />
                        </div>
                    )}

                    {/* Edit — requires a shipment selected */}
                    {onOpenEdit && (
                        <div style={{ opacity: canEdit ? 1 : 0.4, pointerEvents: canEdit ? 'auto' : 'none' }}>
                            <M_Button
                                hoverEffect
                                normal
                                bold
                                label={t('Edit')}
                                icon={<i className='pi pi-pencil' style={{ fontSize: '13px', color: 'var(--color-yellow)' }} />}
                                borderColor={'var(--color-grey)'}
                                onClick={() => {
                                    onOpenEdit(selectedRow)
                                    setSelectedRow(null)
                                }}
                            />
                        </div>
                    )}

                    {value.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginLeft: 'auto', paddingRight: '50px' }}>
                            {[
                                { label: t('TotalInvoice'), val: totalInvoiceSum },
                                { label: t('TotalCost'), val: totalCostSum },
                                { label: t('Revenue'), val: revenueSum },
                            ].map(({ label, val }) => (
                                <span key={label} style={{ fontSize: '13px' }}>
                                    <b style={{ color: secondaryColor }}>{label}:</b>
                                    <span style={{ color: 'var(--color-black)', marginLeft: '4px' }}>{val} €</span>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {isView && value.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'flex-end', padding: '8px 50px 8px 10px' }}>
                    {[
                        { label: t('TotalInvoice'), val: totalInvoiceSum },
                        { label: t('TotalCost'), val: totalCostSum },
                        { label: t('Revenue'), val: revenueSum },
                    ].map(({ label, val }) => (
                        <span key={label} style={{ fontSize: '13px' }}>
                            <b style={{ color: secondaryColor }}>{label}:</b>
                            <span style={{ color: 'var(--color-black)', marginLeft: '4px' }}>{val} €</span>
                        </span>
                    ))}
                </div>
            )}

            <div className='divTable' style={{ overflowX: 'auto' }}>
                <DataTable
                    value={value}
                    emptyMessage={t('NoResults')}
                    size='small'
                    className='tableEquipments'
                    selectionMode={isView ? null : 'single'}
                    selection={isView ? null : selectedRow}
                    onSelectionChange={isView ? undefined : (e => setSelectedRow(e.value))}
                    dataKey='Id'
                >
                    {!isView && (
                    <Column
                        selectionMode='single'
                        headerStyle={hStyle}
                        style={{ width: '3rem' }}
                    />
                    )}
                    <Column
                        key='shipment'
                        header={t('Shipment')}
                        headerStyle={hStyle}
                        body={shipmentCell}
                    />
                    {columns.map(col => (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            headerStyle={hStyle}
                            body={(r) => cell(r, col.field)}
                        />
                    ))}
                    <Column
                        key='cost_value'
                        header={t('CostValue')}
                        headerStyle={hStyle}
                        body={(r) => decimalCell(r, 'CostValue', 'CostCurrencySymbol')}
                    />
                    <Column
                        key='invoice'
                        header={t('Invoice')}
                        headerStyle={hStyle}
                        body={(r) => decimalCell(r, 'Invoice', 'CurrencySymbol')}
                    />
                    <Column
                        key='total_cost'
                        header={t('TotalCost')}
                        headerStyle={hStyle}
                        body={(r) => decimalCell(r, 'TotalCost', '€')}
                    />
                    <Column
                        key='total_invoice'
                        header={t('TotalInvoice')}
                        headerStyle={hStyle}
                        body={(r) => decimalCell(r, 'TotalInvoice', '€')}
                    />
                    <Column
                        key='revenue'
                        header={t('Revenue')}
                        headerStyle={hStyle}
                        body={(r) => decimalCell(r, 'Revenue', '€')}
                    />
                    {onRefresh && (
                        <Column
                            key='delete'
                            header=''
                            headerStyle={hStyle}
                            body={deleteCell}
                            style={{ width: '50px' }}
                        />
                    )}
                </DataTable>
            </div>

            <M_MessageBoard
                warning
                open={!!chargeToDisable}
                onClose={() => setChargeToDisable(null)}
                title={`${t('Delete')} ${t('ShipmentCharge')}?`}
                body={<div>Are you sure you want to delete this <b>{t('ShipmentCharge')}</b>?</div>}
                footer={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <M_Button
                            normal hoverEffect
                            label={t('ButtonCancel')}
                            backgroundColor='var(--color-green)'
                            labelColor='white'
                            onClick={() => setChargeToDisable(null)}
                        />
                        <div style={{ opacity: disabling ? 0.4 : 1, pointerEvents: disabling ? 'none' : 'auto' }}>
                            <M_Button
                                normal hoverEffect
                                label={t('ButtonConfirm')}
                                backgroundColor='var(--color-red)'
                                labelColor='white'
                                onClick={confirmDisable}
                            />
                        </div>
                    </span>
                }
            />
        </div>
    )
}
